import { isNonEmptyString } from "Boards/Utils/string";
import { COLUMN_TYPES } from "Boards/const";


/**
 * Simply returns back the value
 * Useful for the original Boards column types
 * @param {string|undefined} value
 * @returns {string|undefined}
 */
function identity(value) {
  return value;
}

/**
 * Attempts to return the value from a JSON string
 * Useful for simple extended fields
 * @param {string|undefined} json
 * @returns {string}
 *
 * @example
 * getValueOffJson('{ "value": "7" }')
 * => "7"
 */
export function getValueOffJson(json) {
  if (!isNonEmptyString(json)) return "";

  try {
    let { value } = JSON.parse(json);

    if (typeof value === "string") {
      // replace newlines with a space
      value = value.replace(/(?:<br>)/g, " "); // profile multiline text editor adds <br> tags
      value = value.replace(/(?:<\/p>)/g, " "); // signups multiline text editor adds <p> tags
      value = value.replace(/(?:<p>)/g, ""); // signups multiline text editor adds <p> tags
    }

    return value;
  } catch (e) {
    console.error(e.message);
    console.info(
      "Above error occurred while attempting to parse a value configured with getValueOffJson: ",
      json
    );
    return "";
  }
}

/**
 * @param {string|undefined} dateJson
 * @returns {string}
 *
 * @example
 * getDateOffJson('{ "month": "8", "day": "30", "year": 2022 }')
 * => "September 30, 2022"
 *
 * @example
 * getDateOffJson('{ "month": "8", "day": "30", "year": null }')
 * => "September 30"
 */
function getDateOffJson(dateJson) {
  if (!isNonEmptyString(dateJson)) return "";

  try {
    const parsed = JSON.parse(dateJson);
    const { month, day, year } = parsed;
    return new Date(
      // if we don't have the year, it shouldn't be included in the result anyway,
      // so we use a hardcoded Number since it is more efficient than creating a new (but accurate) Date :)
      // i.e. new Date().getFullYear()
      year ?? 1999,
      +month - 1,
      day
    ).toLocaleDateString(LOCALE, {
      month: "long",
      day: "numeric",
      ...(year ? { year: "numeric" } : {}),
    });
  } catch (e) {
    console.error(e.message);
    console.info(
      "Above error occurred while attempting to parse a value configured with getDateOffJson: ",
      dateJson
    );
    return "";
  }
}

/**
 * @param {string|undefined} dateTimeJson
 * @returns {string}
 *
 * @example
 * getDateTimeOffJson('{ "timestamp": 1688145205 }')
 * => "06/30/2023 5:13 PM"
 */
function getDateTimeOffJson(dateTimeJson) {
  if (!isNonEmptyString(dateTimeJson)) return "";

  try {
    const parsed = JSON.parse(dateTimeJson);
    // UNIX timestamp
    const { timestamp } = parsed;
    return new Date(timestamp * 1000).toLocaleString(LOCALE, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    console.error(e.message);
    console.info(
      "Above error occurred while attempting to parse a value configured with getDateTimeOffJson: ",
      dateTimeJson
    );
    return "";
  }
}

/**
 * @param {string|undefined} addressJson
 * @returns {string}
 *
 * @example
 * getAddressOffJson('{"country":"US","addr1":"1234 Elm St","city":"Sciota","state":"PA","zip":"22901"}')
 * => "1234 Elm St, Sciota, PA 22901"
 */
function getAddressOffJson(addressJson) {
  if (!isNonEmptyString(addressJson)) return "";

  try {
    const {
      addr1 = "",
      city = "",
      state = "",
      zip = "",
    } = JSON.parse(addressJson);
    if(!city) return `${addr1}||${state} ${zip}`;
    return `${addr1}||${city}, ${state} ${zip}`;
  } catch (e) {
    console.error(e.message);
    console.info(
      "Above error occurred while attempting to parse a value configured with getAddressOffJson: ",
      addressJson
    );
    return "";
  }
}

/**
 * @param {string|undefined} fileJson
 * @returns {string}
 *
 * @example
 * getfileNameOffJson('{"secureFileID":210,"fileName":"care112.png"}')
 * => "care112.png"
 */
function getFileDataOffJson(fileJson) {
  if (!isNonEmptyString(fileJson)) return "";
  try {
    const { fileName, entityFieldResponseID } = JSON.parse(fileJson);
    return {
      fileName,
      href: `/extendedField/document/download?entityFieldResponseID=${entityFieldResponseID}`,
      entityFieldResponseID,
    };
  } catch (e) {
    console.error(e.message);
    console.info(
      "Above error occurred while attempting to parse a value configured with getAddressOffJson: ",
      fileJson
    );
    return "";
  }
}

/**
 * @param {string} displayProp
 * @returns {function(selectListValuesJson:(string|undefined)): string}
 *
 * @example
 * createSelectListValuesParser("itemTitle")('{values:[{"signupItemID":1,"itemTitle":"Item 1"},{"signupItemID":2,"itemTitle":"Item 2"}]}')
 * => "Item 1, Item 2"
 */
function createSelectListValuesParser(displayProp) {
  return function (selectListValuesJson) {
    if (!isNonEmptyString(selectListValuesJson)) return "";

    try {
      const { values } = JSON.parse(selectListValuesJson);
      if (!(Array.isArray(values) && values.length > 0)) return "";

      let itemTitleValues = values[0][displayProp];
      for (let i = 1; i < values.length; ++i) {
        itemTitleValues += `, ${values[i][displayProp]}`;
      }
      return itemTitleValues;
    } catch (e) {
      console.error(e.message);
      console.info(
        "Above error occurred while attempting to parse a value configured with getPickerValuesOffJson: ",
        selectListValuesJson
      );
      return "";
    }
  };
}

/**
 * We can't handle it
 * @param {*} value
 * @returns {string} Just an empty string for now
 */
function notImplemented(value) {
  // Useful for debugging while implementing new cells
  // console.info("Have not yet implemented a parser for value: ", value);
  return "";
}

export function getValueParser(columnType) {
  switch (columnType) {
    case COLUMN_TYPES.BOOL:
    case COLUMN_TYPES.TEXT:
    case COLUMN_TYPES.NUMBER:
    case COLUMN_TYPES.SIGNUPS_CONFIRMATION_NUMBER:
    case COLUMN_TYPES.SIGNUPS_TOTAL_NUMBER_OF_ITEMS:
    case COLUMN_TYPES.SIGNUPS_NUMBER_OF_ITEMS:
    case COLUMN_TYPES.SIGNUPS_HAS_BEEN_PAID_FOR:
      return identity;
    case COLUMN_TYPES.EXTENDED_FIELDS_TEXT:
    case COLUMN_TYPES.EXTENDED_FIELDS_NUMBER:
    case COLUMN_TYPES.SACRAMENT_BOOK_RECORD:
    case COLUMN_TYPES.SACRAMENT_CHURCH:
      return getValueOffJson;
    case COLUMN_TYPES.EXTENDED_FIELDS_DATE:
      return getDateOffJson;
    case COLUMN_TYPES.SIGNUPS_SIGNED_UP_DATE:
      return getDateTimeOffJson;
    case COLUMN_TYPES.EXTENDED_FIELDS_ADDRESS:
      return getAddressOffJson;
    case COLUMN_TYPES.EXTENDED_FIELDS_SELECT_LIST:
    case COLUMN_TYPES.SIGNUPS_ITEMS_SIGNED_UP_FOR:
      return createSelectListValuesParser(
        getSelectListDisplayValueAccessor(columnType)
      );
    case COLUMN_TYPES.EXTENDED_FIELDS_FILE:
      return getFileDataOffJson;
    default:
      return notImplemented;
  }
}

function getSelectListDisplayValueAccessor(columnType) {
  switch (columnType) {
    case COLUMN_TYPES.EXTENDED_FIELDS_SELECT_LIST:
      return "optionListValue";
    case COLUMN_TYPES.SIGNUPS_ITEMS_SIGNED_UP_FOR:
      return "itemTitle";
    default:
      return undefined;
  }
}

const LOCALE = window.navigator.language ?? "en-US";